/**
 * @packageDocumentation
 * @module f1/asynchronous
 */

import map from '../adt/map';
import task, { TTask } from './task';

export interface Parallel {
  /**
   * Run all asynchronous tasks in parallel; just like `Promise.all`
   *
   * @public
   * @function f1.parallel
   * @param tasks a list of tasks to execute
   * @example
   *
   * ```ts
   * f1.parallel([
   *   () => fetch('1.json'),
   *   () => fetch('2.json')
   * ], f1.skip).then(([_1, _2]) => ({
   *   _1,
   *   _2
   * }));
   * ```
   *
   */
  <E, R>(tasks: TTask<E, R>[], value: E): Promise<unknown[]>;
  <E, R>(tasks: TTask<E, R>[]): (value: E) => Promise<unknown[]>;
}

/**
 * @see Parallel
 */
export default <E, R>(tasks: TTask<E, R>[], value: E): Promise<unknown[]> => {
  return Promise.all(map((t: TTask<E, R>) => task<E, R>(t, value), tasks) as Iterable<unknown>);
};
